/**
 * Piccard UI Library - RatingButtons Component
 *
 * SRS rating grid with color-coded buttons
 * Again/Hard/Good/Easy with flat editorial styling
 */

import { cn } from '@/lib/utils'

export type Rating = 'again' | 'hard' | 'good' | 'easy'

export interface RatingButtonsProps {
  onRate: (rating: Rating) => void
  onMastered?: () => void
  mastering?: boolean
  disabled?: boolean
  className?: string
}

const RATING_CONFIG = [
  {
    value: 'again' as Rating,
    label: 'Again',
    icon: 'history',
    bg: 'bg-error',
  },
  {
    value: 'hard' as Rating,
    label: 'Hard',
    icon: 'sentiment_dissatisfied',
    bg: 'bg-accent-amber',
  },
  {
    value: 'good' as Rating,
    label: 'Good',
    icon: 'thumb_up',
    bg: 'bg-accent-teal',
    fill: true,
  },
  {
    value: 'easy' as Rating,
    label: 'Easy',
    icon: 'bolt',
    bg: 'bg-success',
    fill: true,
  },
]

const MASTER_CONFIG = {
  label: 'Master',
  icon: 'emoji_events',
  bg: 'bg-accent-amber',
}

export function RatingButtons({
  onRate,
  onMastered,
  mastering = false,
  disabled = false,
  className,
}: RatingButtonsProps) {
  return (
    <div className={cn('w-full space-y-3', className)}>
      <div className="flex justify-center w-full">
        <div className={cn(
          'grid gap-3 w-full max-w-[280px]',
          onMastered ? 'grid-cols-5' : 'grid-cols-4',
        )}>
          {RATING_CONFIG.map(({ value, label, icon, bg, fill }) => (
            <button
              key={value}
              type="button"
              onClick={() => onRate(value)}
              disabled={disabled}
              aria-label={`Rate as ${label}`}
              className="group flex flex-col items-center gap-2 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div
                className={cn(
                  'w-14 h-14 rounded-full shadow-subtle flex items-center justify-center text-white transition-shadow hover:shadow-card',
                  bg
                )}
              >
                <span
                  className="material-symbols-outlined text-2xl"
                  style={{ fontVariationSettings: fill ? "'FILL' 1, 'wght' 600" : "'wght' 600" }}
                >
                  {icon}
                </span>
              </div>
              <span className="text-[10px] font-black uppercase text-muted tracking-tight">
                {label}
              </span>
            </button>
          ))}
          {onMastered && (
            <button
              type="button"
              onClick={onMastered}
              disabled={disabled || mastering}
              aria-label="Mark as mastered"
              className="group flex flex-col items-center gap-2 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div
                className={cn(
                  'w-14 h-14 rounded-full shadow-subtle flex items-center justify-center text-white transition-shadow hover:shadow-card',
                  MASTER_CONFIG.bg
                )}
              >
                <span
                  className="material-symbols-outlined text-2xl"
                  style={{ fontVariationSettings: "'FILL' 1, 'wght' 600" }}
                >
                  {MASTER_CONFIG.icon}
                </span>
              </div>
              <span className="text-[10px] font-black uppercase text-muted tracking-tight">
                {mastering ? '...' : MASTER_CONFIG.label}
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
